import React, { FC, useEffect } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { connect } from 'react-redux';
import getCurrencySymbol from 'dibs-currency-helpers/getCurrencySymbol';
import { setLocalCurrency, validCurrency } from '../../../utils/preferredCurrency';
import { actionCreators } from '../../../actions';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { useIntl, defineMessages } from 'react-intl';

import { changeCurrencyMutation } from './changeCurrencyMutation';
import { CurrencyProvider } from '../../CurrencyProvider/CurrencyProvider';
import Dropdown from '../Dropdown';
import { CurrencySelector_viewer } from './__generated__/CurrencySelector_viewer.graphql';
import { CurrencySelector_user } from './__generated__/CurrencySelector_user.graphql';

type PopulateCurrency = (currency: string) => void;

type Props = {
    relay: RelayProp;
    viewer: CurrencySelector_viewer;
    user: CurrencySelector_user;
    populateCurrency: PopulateCurrency;
};

const messages = defineMessages({
    label: {
        id: 'dbl.Footer.CurrencySelector.label',
        defaultMessage: 'Currency',
    },
});

const CurrencySelector: FC<Props> = ({ populateCurrency, relay, viewer, user }) => {
    const intl = useIntl();
    useEffect(() => {
        const currencyParam = validCurrency(
            new URLSearchParams(document.location.search).get('currency') || ''
        );

        if (currencyParam) {
            setLocalCurrency(currencyParam);
            populateCurrency(currencyParam);
        }
    }, [populateCurrency]);

    function updateCurrencyComplete(currency: string): void {
        setLocalCurrency(currency);
        populateCurrency(currency);
        // strip out forced currency so that you can change currency when present :
        const urlParams = new URLSearchParams(document.location.search);
        if (urlParams.get('currency')) {
            urlParams.set('currency', currency);
            window.location.search = urlParams.toString(); // reloads page
        } else {
            window.location.reload();
        }
    }

    function updateCurrency(currency: string): void {
        if (user) {
            changeCurrencyMutation({
                environment: relay.environment,
                user: user,
                currency,
                onCompleted: () => updateCurrencyComplete(currency),
            });
        } else {
            updateCurrencyComplete(currency);
        }
    }

    function handleCurrencyChange(currency: string): void {
        const category = 'navigation';
        const action = 'footer navigation click';
        const label = `change currency|${currency.toLowerCase()}`;
        const value = 0;
        const additional = { defaultCurrency: `${currency} Selected` };

        trackEvent({ category, action, label, value, additional });

        updateCurrency(currency);
    }

    const currencies =
        viewer?.currencies?.map(currency =>
            currency
                ? {
                      key: `${getCurrencySymbol(currency)} ${currency}`,
                      value: currency,
                  }
                : null
        ) || [];

    return (
        <CurrencyProvider
            render={({ currency }: { currency: string }) => (
                <Dropdown
                    dataTn="footer-currency-dropdown"
                    selectedOption={`${getCurrencySymbol(currency)} ${currency}`}
                    options={currencies.filter(filterNulls)}
                    onChange={handleCurrencyChange}
                    label={intl.formatMessage(messages.label)}
                />
            )}
        />
    );
};

const mapDispatchToProps = (
    dispatch: (cbArg: { type: string }) => void
): { populateCurrency: PopulateCurrency } => {
    return {
        populateCurrency: currency => dispatch(actionCreators.populateCurrency(currency)),
    };
};

export default createFragmentContainer(connect(null, mapDispatchToProps)(CurrencySelector), {
    viewer: graphql`
        fragment CurrencySelector_viewer on Viewer {
            currencies
        }
    `,
    user: graphql`
        fragment CurrencySelector_user on User {
            id
            serviceId
            profile {
                currency
            }
        }
    `,
});
